// src/pages/ia-paca/metiers/expert-ia-generative.tsx
//
// Page métier — Expert IA générative en PACA.

import MetierPageTemplate, {
  type MetierPageConfig,
} from '../../../components/ia-paca/MetierPageTemplate';
import { Sparkles, Image as ImageIcon, Mic, Code2, Activity, ShieldCheck } from 'lucide-react';

const CONFIG: MetierPageConfig = {
  slug: 'expert-ia-generative',
  metaTitle: 'Recrutement Expert IA générative en PACA — Marseille, Sophia | recruteurs.io',
  metaDescription:
    "Recruter un Expert IA générative en PACA : génération texte, image, audio, vidéo. Multimodal, diffusion models, GenAI produits. Salaire 60-130k€.",
  metaKeywords:
    'recrutement Expert IA générative, GenAI Marseille, Expert GenAI Sophia, salaire Expert IA générative 2026, recruter GenAI PACA, diffusion models, multimodal',
  heroLabel: 'Expert IA générative',
  heroSubtitle:
    "Pas un AI Engineer généraliste. Un profil qui maîtrise le spectre génératif complet : texte, image, audio, vidéo, et leur intégration produit. Pour les boîtes qui veulent intégrer la GenAI dans leur offre — créatives, e-commerce, MedTech, gaming.",
  heroBadges: ['Multimodal', 'Diffusion', 'GenAI produit', 'Audio / Image'],
  jsonLdName: 'Expert IA générative',
  jsonLdDescription:
    "L'Expert IA générative maîtrise la création multimodale par IA : génération de texte (LLM), image (Stable Diffusion, Flux, DALL-E), audio (Whisper, TTS, Eleven Labs), vidéo (Runway, Pika, Sora-likes).",
  jsonLdSkills:
    'Stable Diffusion / Flux / Imagen, ComfyUI, Automatic1111, LoRA / ControlNet, ESRGAN / upscaling, Whisper, OpenAI TTS, ElevenLabs, Suno / Udio, Runway / Pika, multimodal LLM (GPT-4o, Claude Vision), prompt engineering génératif avancé',
  jsonLdMinSalary: 60000,
  jsonLdMedianSalary: 90000,
  jsonLdMaxSalary: 130000,
  jsonLdEducation: 'Background mixte : ingé ML + créatif (UI/UX, design, audio, vidéo).',
  jsonLdExperience: '2-10+ ans, profil émergent.',
  intro: {
    title: "Expert IA générative : le pont entre la création et l'industrialisation.",
    paragraphs: [
      "L'Expert IA générative <strong>n'est pas un freelance qui prompt Midjourney</strong>. C'est un profil tech qui intègre la création générative dans un produit ou un workflow industriel. Il sait quels modèles utiliser pour quel usage, comment fine-tuner un LoRA pour préserver l'identité visuelle d'une marque, comment scale un pipeline d'images de produits pour 10 000 SKU.",
      "Concrètement : il travaille avec Stable Diffusion / Flux pour l'image, Whisper et ElevenLabs pour l'audio, Runway / Pika pour la vidéo, et combine le tout via des workflows ComfyUI ou des APIs. Il sait évaluer la qualité des sorties (visuelle, sonore), gérer les droits d'auteur des datasets, et maîtriser les biais et risques (deepfake, contenu sensible).",
      "<strong>Marché PACA :</strong> très demandé par les scale-ups créatives, e-commerce (génération de visuels produits), MedTech (synthèse d'imagerie pour augmentation de datasets), gaming (génération procédurale assistée). Profil émergent, peu nombreux, packages bien payés.",
    ],
  },
  stackTitle: 'Les outils que votre Expert GenAI doit maîtriser.',
  stack: [
    {
      Icon: ImageIcon,
      title: 'Génération image',
      tools: ['Stable Diffusion (SDXL, 3.5)', 'Flux', 'Imagen', 'DALL-E 3', 'ComfyUI', 'Automatic1111'],
    },
    {
      Icon: Sparkles,
      title: 'Customisation image',
      tools: ['LoRA training', 'DreamBooth', 'ControlNet', 'IP-Adapter', 'inpainting / outpainting'],
    },
    {
      Icon: Mic,
      title: 'Génération audio',
      tools: ['ElevenLabs', 'OpenAI TTS', 'Whisper (STT)', 'Suno / Udio (musique)', 'voice cloning'],
    },
    {
      Icon: Code2,
      title: 'Vidéo générative',
      tools: ['Runway Gen-3', 'Pika', 'Luma Dream Machine', 'Kling', 'video2video pipelines'],
    },
    {
      Icon: Activity,
      title: 'Multimodal LLM',
      tools: ['GPT-4o (vision + voice)', 'Claude Vision', 'Gemini Multimodal', 'LLaVA', 'Qwen2-VL'],
    },
    {
      Icon: ShieldCheck,
      title: 'Sécurité & droits',
      tools: ['Watermarking (C2PA)', 'détection deepfake', 'NSFW filtering', 'droits dataset', 'AI Act créatif'],
    },
  ],
  salaryByCityTitle: '60-130 k€, fort premium sur expérience produit + identité visuelle marque.',
  salaryByCity: [
    { ville: 'Marseille', junior: '60 – 70 k€', confirme: '70 – 95 k€', senior: '95 – 120 k€', expert: '115 – 135 k€' },
    { ville: 'Aix-en-Provence', junior: '58 – 68 k€', confirme: '68 – 92 k€', senior: '92 – 115 k€', expert: '110 – 130 k€' },
    { ville: 'Sophia Antipolis', junior: '65 – 75 k€', confirme: '75 – 100 k€', senior: '100 – 125 k€', expert: '120 – 140 k€' },
    { ville: 'Nice', junior: '62 – 72 k€', confirme: '72 – 98 k€', senior: '98 – 122 k€', expert: '118 – 138 k€' },
  ],
  questionsTitle: '5 questions pour qualifier un Expert IA générative.',
  questionsIntro:
    "Ces questions séparent un Expert GenAI industriel d'un utilisateur avancé de Midjourney.",
  questions: [
    {
      q: "Tu dois générer 10 000 visuels produits e-commerce respectant l'identité visuelle d'une marque. Comment tu structures le pipeline ?",
      good:
        "Décrit : 1) base model (SDXL / Flux), 2) LoRA marque entraîné sur 50-200 images de référence, 3) ControlNet pour le cadrage produit, 4) IP-Adapter pour la consistance, 5) pipeline ComfyUI ou code Python avec batching, 6) review humaine sur 5-10% des sorties, 7) monitoring qualité (CLIP similarity, brand consistency score).",
      bad: "'Je prompte Midjourney en boucle.' Aucune notion d'industrialisation, pas de consistance marque, droits d'usage flous.",
    },
    {
      q: 'Quelle est la différence entre LoRA, DreamBooth, Textual Inversion, ControlNet, et IP-Adapter ?',
      good:
        "LoRA = adapters légers entraînés sur un dataset spécifique. DreamBooth = fine-tune complet du modèle (plus puissant mais lourd). Textual Inversion = embed d'un nouveau token, sans toucher au modèle. ControlNet = guide spatial (pose, depth, canny). IP-Adapter = guide par image de référence. Sait quand utiliser chacun.",
      bad: "Confond ou ne connaît pas tous ces outils.",
    },
    {
      q: 'Tu dois cloner une voix pour un assistant vocal. Quels sont les enjeux techniques et éthiques ?',
      good:
        "Technique : ElevenLabs / OpenAI Voice Engine / XTTS, qualité dépend du dataset (5-10 min audio propre), prosodie, accent. Éthique : consentement explicite + écrit, biométrie vocale = donnée sensible (RGPD), risque de deepfake, watermarking C2PA, AI Act voice cloning = transparence obligatoire (l'utilisateur doit savoir que c'est une voix synthétique).",
      bad: "Ignore les enjeux éthiques. Propose de cloner une voix sans consentement explicite.",
    },
    {
      q: 'Comment tu évalues la qualité des sorties génératives ?',
      good:
        "Mix d'évaluation : 1) métriques automatiques (CLIP score pour image-text, FID pour qualité globale, LPIPS pour similarité perceptuelle), 2) brand consistency score si LoRA marque, 3) review humaine systématique sur un échantillon, 4) A/B testing en prod, 5) feedback utilisateur. Connaît la limite des métriques auto sur la créativité.",
      bad: "'Je regarde si c'est joli.' Pas de process d'éval, qualité non maîtrisée à l'échelle.",
    },
    {
      q: "Comment tu gères les droits d'auteur des datasets utilisés pour fine-tuner ?",
      good:
        "Connaît les enjeux : data scraping vs data licenciée, exception TDM (text and data mining) RGPD article 4, opt-out créateurs, modèles entraînés sur datasets publics (LAION) vs datasets propriétaires, contractualisation avec la marque (licence d'utilisation des images de réf). Conseille sur les risques juridiques.",
      bad: "'On utilise tout ce qui est sur internet.' Risque juridique majeur pour le client.",
    },
  ],
  ctaTitle: 'Vous cherchez un Expert IA générative en PACA ?',
  ctaSubtitle:
    "Profil émergent et rare. Notre court-list cible 3-5 candidats avec expérience produit en 20-30 jours.",
};

export default function ExpertIaGenerativePage() {
  return <MetierPageTemplate config={CONFIG} />;
}
